import ibmPlexSansBoldTtf from "@/assets/fonts/IBMPlexSans/IBMPlexSans-Bold.ttf";
import ibmPlexSansRegularTtf from "@/assets/fonts/IBMPlexSans/IBMPlexSans.ttf";
import {PresentationPopup} from "@/components/popups/requestPopups/PresentationPopup";
import {Try} from "@/pages/common/Home/components/Try";

import React, {useCallback, useMemo} from "react";

import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {H3} from "@ui/titles/H3";
import {Helmet} from "react-helmet";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {AccordeonSection} from "./AccordeonSection";
import {Analysis} from "./Analysis";
import {Calc} from "./Calc";
import {Conclusion} from "./Conclusion";
import {
  EffectivenessContext,
  useEffectivenessPage,
} from "./EffectivenessContext";
import {Intro} from "./Intro";
import {Libs} from "./Libs";
import styles from "./styles.module.scss";

const PDF_FONT_FAMILY = "NotoSans";
let pdfFontLoadPromise = null;

function arrayBufferToBinaryString(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }

  return binary;
}

async function ensurePdfUnicodeFont(doc) {
  if (!pdfFontLoadPromise) {
    pdfFontLoadPromise = (async () => {
      const [regularRes, boldRes] = await Promise.all([
        fetch(ibmPlexSansRegularTtf),
        fetch(ibmPlexSansBoldTtf),
      ]);

      if (!regularRes.ok || !boldRes.ok) {
        throw new Error("Failed to load unicode fonts for PDF");
      }

      const [regularBuffer, boldBuffer] = await Promise.all([
        regularRes.arrayBuffer(),
        boldRes.arrayBuffer(),
      ]);

      return {
        regular: arrayBufferToBinaryString(regularBuffer),
        bold: arrayBufferToBinaryString(boldBuffer),
      };
    })();
  }

  const fontData = await pdfFontLoadPromise;
  doc.addFileToVFS("NotoSans-Regular.ttf", fontData.regular);
  doc.addFont("NotoSans-Regular.ttf", PDF_FONT_FAMILY, "normal");
  doc.addFileToVFS("NotoSans-Bold.ttf", fontData.bold);
  doc.addFont("NotoSans-Bold.ttf", PDF_FONT_FAMILY, "bold");
}

function toPlainText(value = "") {
  return String(value)
    .replace(/<li>/gi, "\n- ")
    .replace(/<\/li>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function useCostSavingsAccordeonSection({
  roiFromConfig,
  config,
  formatInteger,
  currencySymbol,
}) {
  const {t} = useTranslation();

  return useMemo(() => {
    const sectionPath = "page.effectiveness.cost_savings";
    const fmtEuro = (n) =>
      `${currencySymbol}${formatInteger(Math.round(Number(n) || 0))}`;
    const bl = config.baselines;
    const asm = config.assumptions;
    const wf = config.workforce;
    const org = config.organization ?? {};
    const dynamicNewcomers = Math.round(
      (Number(wf.number_of_medical_representatives) || 0) *
        ((Number(asm.newcomers_share_percent) || 10) / 100),
    );
    const dynamicManagers = Math.round(
      (Number(wf.number_of_medical_representatives) || 0) /
        (Number(asm.manager_span_of_control) || 13),
    );
    const complianceEfficiencyPct = Math.round(
      (Number(org.efficiency_gain_ratio) || 0) * 100,
    );
    const complianceCostBaseline = formatInteger(
      Math.round(Number(org.current_annual_cost_total_organization) || 0),
    );

    const bulletsHtml = (bullets) =>
      (Array.isArray(bullets) ? bullets : [])
        .map((bullet) => `<li>${bullet}</li>`)
        .join("");

    const block = (itemNum, summaryVars, descriptionVars, amountRaw) => {
      const key = String(itemNum);
      const head = t(`${sectionPath}.items.${key}.head`);
      const bullets = t(`${sectionPath}.items.${key}.bullets`, {
        returnObjects: true,
      });
      const amountStr = fmtEuro(amountRaw);
      const summary = t(`${sectionPath}.items.${key}.summary`, {
        ...summaryVars,
        amount: amountStr,
      });
      const description = t(`${sectionPath}.items.${key}.description`, {
        ...descriptionVars,
        amount: amountStr,
      });
      const value = t(`${sectionPath}.items.${key}.value`, {amount: amountStr});
      const bodyHtml = `${summary}<br /><br />${description}<ul>${bulletsHtml(bullets)}</ul>`;

      return {
        id: itemNum - 1,
        head,
        valueLabel: t(`${sectionPath}.value_label`),
        value,
        body: bodyHtml,
        bodyIsHtml: true,
      };
    };

    const a1 = roiFromConfig.ESTIMATED_ANNUAL_SAVINGS;
    const a2 = roiFromConfig.ESTIMATED_VALUE_OF_FREED_TIME;
    const a3 = roiFromConfig.ESTIMATED_ANNUAL_EFFICIENCY_VALUE;

    const items = [
      block(
        1,
        {
          reduction_pct: asm.onboarding_cost_reduction_percent,
          baseline_per_rep: formatInteger(bl.current_onboarding_cost_per_rep),
          reps: formatInteger(dynamicNewcomers),
        },
        {
          baseline_per_rep: formatInteger(bl.current_onboarding_cost_per_rep),
          reduction_pct: asm.onboarding_cost_reduction_percent,
          reps: formatInteger(dynamicNewcomers),
        },
        a1,
      ),
      block(
        2,
        {
          hours: asm.manager_time_freed_hours_per_month_per_manager,
          managers: dynamicManagers,
          hourly_rate: formatInteger(Math.round(bl.manager_hourly_rate)),
        },
        {},
        a2,
      ),
      block(
        3,
        {
          efficiency_pct: complianceEfficiencyPct,
          compliance_baseline: complianceCostBaseline,
        },
        {
          compliance_baseline: complianceCostBaseline,
          efficiency_pct: complianceEfficiencyPct,
        },
        a3,
      ),
    ];

    const total = t(`${sectionPath}.items.total`, {
      amount: fmtEuro(roiFromConfig.SUBTOTAL_COST_SAVINGS),
    });

    return {
      title: t(`${sectionPath}.block_title`),
      items,
      total,
    };
  }, [t, roiFromConfig, config, formatInteger, currencySymbol]);
}

function useImpactAccordeonSection({
  roiFromConfig,
  config,
  formatInteger,
  currencySymbol,
  excludePenaltiesBlock,
}) {
  const {t} = useTranslation();

  return useMemo(() => {
    const sectionPath = "page.effectiveness.impact";
    const fmtEuro = (n) =>
      `${currencySymbol}${formatInteger(Math.round(Number(n) || 0))}`;
    const wf = config.workforce;
    const asm = config.assumptions;
    const org = config.organization ?? {};
    const dynamicNewcomers = Math.round(
      (Number(wf.number_of_medical_representatives) || 0) *
        ((Number(asm.newcomers_share_percent) || 10) / 100),
    );
    const violationsCount = Math.max(
      1,
      Math.round(Number(org.pmcpa_violations_prevented_count) || 1),
    );
    const penaltiesTotal = roiFromConfig.ESTIMATED_PENALTIES_PREVENTED;
    const finePerViolation =
      violationsCount > 0 ? penaltiesTotal / violationsCount : 0;

    const bulletsHtml = (bullets) =>
      (Array.isArray(bullets) ? bullets : [])
        .map((bullet) => `<li>${bullet}</li>`)
        .join("");

    const block = (itemNum, summaryVars, descriptionVars, amountRaw) => {
      const key = String(itemNum);
      const head = t(`${sectionPath}.items.${key}.head`);
      const bullets = t(`${sectionPath}.items.${key}.bullets`, {
        returnObjects: true,
      });
      const amountStr = fmtEuro(amountRaw);
      const summary = t(`${sectionPath}.items.${key}.summary`, {
        ...summaryVars,
        amount: amountStr,
      });
      const description = t(`${sectionPath}.items.${key}.description`, {
        ...descriptionVars,
        amount: amountStr,
      });
      const value = t(`${sectionPath}.items.${key}.value`, {amount: amountStr});
      const bodyHtml = `${summary}<br /><br />${description}<ul>${bulletsHtml(bullets)}</ul>`;

      return {
        id: itemNum - 1,
        head,
        valueLabel: t(`${sectionPath}.value_label`),
        value,
        body: bodyHtml,
        bodyIsHtml: true,
      };
    };

    const newcomersStr = formatInteger(dynamicNewcomers);

    const items = [
      block(
        1,
        {newcomers: newcomersStr},
        {newcomers: newcomersStr},
        roiFromConfig.ESTIMATED_PRODUCTIVITY_VALUE,
      ),
      block(
        2,
        {sales_uplift_pct: asm.estimated_sales_uplift_percent},
        {sales_uplift_pct: asm.estimated_sales_uplift_percent},
        roiFromConfig.ESTIMATED_GROSS_PROFIT_IMPACT,
      ),
      ...(excludePenaltiesBlock
        ? []
        : [
            block(
              3,
              {
                violations: violationsCount,
                fine_per_violation: fmtEuro(finePerViolation),
              },
              {},
              penaltiesTotal,
            ),
          ]),
    ];

    const total = t(`${sectionPath}.items.total`, {
      amount: fmtEuro(roiFromConfig.SUBTOTAL_REVENUE_RISK_MITIGATION),
    });

    return {
      title: t(`${sectionPath}.block_title`),
      items,
      total,
    };
  }, [
    t,
    roiFromConfig,
    config,
    formatInteger,
    currencySymbol,
    excludePenaltiesBlock,
  ]);
}

function useAnalysisAccordeonSection({
  roiFromConfig,
  config,
  formatInteger,
  currencySymbol,
}) {
  const {t} = useTranslation();

  return useMemo(() => {
    const sectionPath = "page.effectiveness.analysis";
    const fmtEuro = (n) =>
      `${currencySymbol}${formatInteger(Math.round(Number(n) || 0))}`;
    const months = config.timeline.number_of_months;

    const bulletsHtml = (bullets) =>
      (Array.isArray(bullets) ? bullets : [])
        .map((bullet) => `<li>${bullet}</li>`)
        .join("");

    const block = (itemNum, summaryExtra, amountRaw) => {
      const key = String(itemNum);
      const head = t(`${sectionPath}.items.${key}.head`);
      const bullets = t(`${sectionPath}.items.${key}.bullets`, {
        returnObjects: true,
      });
      const amountStr = fmtEuro(amountRaw);
      const summary = t(`${sectionPath}.items.${key}.summary`, {
        ...summaryExtra,
        amount: amountStr,
      });
      const value = t(`${sectionPath}.items.${key}.value`, {amount: amountStr});
      const bodyHtml = `${summary}<br /><br /><ul>${bulletsHtml(bullets)}</ul>`;
      return {
        id: itemNum - 1,
        head,
        valueLabel: t(`${sectionPath}.value_label`),
        value,
        body: bodyHtml,
        bodyIsHtml: true,
      };
    };

    const items = [
      block(1, {}, roiFromConfig.SUBTOTAL_PILOT),
      block(2, {}, roiFromConfig.ROLLOUT_PLATFORM_FIXED),
    ];

    const total = t(`${sectionPath}.items.total`, {
      months,
      recurring: fmtEuro(roiFromConfig.ROLLOUT_RECURRING_LICENSES_AND_AVATAR),
      total_y1: fmtEuro(roiFromConfig.TOTAL_YEAR_1_INVESTMENT),
    });

    return {
      title: t(`${sectionPath}.block_title`),
      items,
      total,
    };
  }, [t, roiFromConfig, config, formatInteger, currencySymbol]);
}

export const Effectiveness = () => {
  const model = useEffectivenessPage();
  const {
    currencySymbol,
    formatInteger,
    formatDecimal,
    formatPaybackPeriod,
    effectivenessConfigWithReps,
    roiFromConfig,
    scenarioValues,
  } = model;
  const {t, i18n} = useTranslation();
  const {language} = useSelector((state) => state.common.settings);
  const {openPopup} = usePopup();

  const langIsRu = useMemo(
    () => (language || i18n.language || "en").toLowerCase().startsWith("ru"),
    [language, i18n.language],
  );

  const costSavingsSection = useCostSavingsAccordeonSection({
    roiFromConfig,
    config: effectivenessConfigWithReps,
    formatInteger,
    currencySymbol,
  });

  const impactSection = useImpactAccordeonSection({
    roiFromConfig,
    config: effectivenessConfigWithReps,
    formatInteger,
    currencySymbol,
    excludePenaltiesBlock: langIsRu,
  });

  const analysisSection = useAnalysisAccordeonSection({
    roiFromConfig,
    config: effectivenessConfigWithReps,
    formatInteger,
    currencySymbol,
  });

  const keyRecommendationDescriptionHtml = useMemo(() => {
    const fmtEuro = (n) =>
      `${currencySymbol}${formatInteger(Math.round(Number(n) || 0))}`;

    const paybackMonths =
      Number(roiFromConfig.PAYBACK_MONTHS_CONSERVATIVE) || 0;
    const paybackWeeks = Math.round((paybackMonths * 52) / 12);
    const langIsRu = (language || i18n.language || "en")
      .toLowerCase()
      .startsWith("ru");
    const weekCat = new Intl.PluralRules(langIsRu ? "ru-RU" : "en-US").select(
      paybackWeeks,
    );
    const payback_weeks_span = langIsRu
      ? `${paybackWeeks}\u00a0${
          {
            one: "неделю",
            few: "недели",
            many: "недель",
            other: "недель",
          }[weekCat] || "недель"
        }`
      : `${paybackWeeks}\u00a0${t(
          `page.effectiveness.payback.week_${weekCat}`,
          {defaultValue: t("page.effectiveness.payback.week_other")},
        )}`;

    return t("page.effectiveness.recommendations_description", {
      investment: fmtEuro(roiFromConfig.TOTAL_YEAR_1_INVESTMENT),
      payback_weeks_span,
      net_benefit: fmtEuro(roiFromConfig.NET_BENEFIT_CONSERVATIVE),
      pilot_reps: 50,
      scale_reps: 100,
      full_reps: 200,
    });
  }, [
    t,
    i18n.language,
    language,
    roiFromConfig,
    currencySymbol,
    formatInteger,
  ]);

  const pricingNoteRecommendations = useMemo(() => {
    return {
      title: t("page.effectiveness.analysis.recommendations.title"),
      description: t(
        "page.effectiveness.analysis.recommendations.description",
        {
          saas_min: 45,
          saas_max: 60,
          disc_min: 10,
          disc_max: 15,
        },
      ),
    };
  }, [t]);

  const conclusionItems = useMemo(() => {
    const fmtCurrency = (n) =>
      `${currencySymbol}${formatInteger(Math.round(Number(n) || 0))}`;
    const totalInvestment = roiFromConfig.TOTAL_YEAR_1_INVESTMENT;
    const netBenefitYear1 = roiFromConfig.NET_BENEFIT_REVISED;
    const roiPercent = Math.round(roiFromConfig.ROI_REVISED);
    const paybackMonths = roiFromConfig.PAYBACK_MONTHS_REVISED;
    const pilotBudget = roiFromConfig.SUBTOTAL_PILOT;

    return [1, 2, 3, 4].map((idx) => {
      const title = t(`page.effectiveness.conclusion.items.${idx}.title`);

      if (idx === 1) {
        return {
          id: idx,
          title,
          description: t("page.effectiveness.conclusion.items.1.description", {
            investment: fmtCurrency(totalInvestment),
            net_benefit: fmtCurrency(netBenefitYear1),
            roi: roiPercent,
            payback_period: formatPaybackPeriod(paybackMonths),
          }),
        };
      }

      if (idx === 3) {
        return {
          id: idx,
          title,
          description: t("page.effectiveness.conclusion.items.3.description", {
            payback_weeks: formatPaybackPeriod(
              roiFromConfig.PAYBACK_MONTHS_CONSERVATIVE,
            ),
          }),
        };
      }

      if (idx === 4) {
        return {
          id: idx,
          title,
          description: t(
            `page.effectiveness.conclusion.items.${idx}.description`,
            {
              pilot_budget: fmtCurrency(pilotBudget),
            },
          ),
        };
      }

      return {
        id: idx,
        title,
        description: t(
          `page.effectiveness.conclusion.items.${idx}.description`,
        ),
      };
    });
  }, [currencySymbol, formatInteger, formatPaybackPeriod, roiFromConfig, t]);

  const handleOpenPopup = () => {
    openPopup(<PresentationPopup title={t("popup.demo.title")} />);
  };

  const handlePrint = useCallback(async () => {
    const [{default: jsPDF}] = await Promise.all([import("jspdf")]);
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
      compress: true,
    });
    try {
      await ensurePdfUnicodeFont(doc);
    } catch {
      // Keep fallback font behavior if loading fails.
    }
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 28;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const ensureSpace = (need = 24) => {
      if (y + need > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const ensureSectionStart = (estimatedHeight = 180) => {
      if (y + estimatedHeight > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const writeText = (
      text,
      fontSize = 11,
      extraSpace = 6,
      {bold = false, color = [32, 32, 32]} = {},
    ) => {
      if (!text) return;
      doc.setFont(PDF_FONT_FAMILY, bold ? "bold" : "normal");
      doc.setTextColor(color[0], color[1], color[2]);
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(text, contentWidth);
      const blockHeight = lines.length * (fontSize + 2);
      ensureSpace(blockHeight + extraSpace);
      doc.text(lines, margin, y);
      y += blockHeight + extraSpace;
      doc.setTextColor(32, 32, 32);
    };

    const writeSectionRowSpaceBetween = (leftTextRaw, rightTextRaw) => {
      const leftText = toPlainText(leftTextRaw).replace(/\s+/g, " ").trim();
      const rightText = toPlainText(rightTextRaw).replace(/\s+/g, " ").trim();
      const fontSize = 12;
      const minGap = 16;

      ensureSpace(fontSize + 8);
      doc.setFont(PDF_FONT_FAMILY, "normal");
      doc.setFontSize(fontSize);
      doc.setTextColor(32, 32, 32);

      const rightWidth = doc.getTextWidth(rightText);
      const rightX = margin + contentWidth - rightWidth;
      const leftMaxWidth = Math.max(40, contentWidth - rightWidth - minGap);
      const leftLine = doc.splitTextToSize(leftText, leftMaxWidth)[0] || "";

      doc.text(leftLine, margin, y);
      const leftWidth = doc.getTextWidth(leftLine);
      doc.setDrawColor(32, 32, 32);
      doc.setLineWidth(0.6);
      doc.line(margin, y + 2, margin + leftWidth, y + 2);

      doc.setFont(PDF_FONT_FAMILY, "bold");
      doc.setTextColor(valueColor[0], valueColor[1], valueColor[2]);
      doc.text(rightText, rightX, y);
      doc.setTextColor(32, 32, 32);
      y += fontSize + 6;
    };

    const writeRightAlignedTotal = (textRaw) => {
      const lines = toPlainText(textRaw)
        .split("\n")
        .map((line) => line.replace(/\s+/g, " ").trim())
        .filter(Boolean);
      const fontSize = 11;
      if (!lines.length) return;
      ensureSpace(lines.length * (fontSize + 3) + 8);
      doc.setFont(PDF_FONT_FAMILY, "bold");
      doc.setFontSize(fontSize);
      doc.setTextColor(valueColor[0], valueColor[1], valueColor[2]);
      lines.forEach((line, index) => {
        const textWidth = doc.getTextWidth(line);
        const x = margin + contentWidth - textWidth;
        doc.text(line, x, y + index * (fontSize + 3));
      });
      doc.setTextColor(32, 32, 32);
      y += lines.length * (fontSize + 3) + 6;
    };

    const writeTitle = (text, size = 16, withDivider = false) => {
      ensureSpace(size + (withDivider ? 24 : 10));
      doc.setFont(PDF_FONT_FAMILY, "bold");
      doc.setTextColor(16, 16, 16);
      doc.setFontSize(size);
      doc.text(text, margin, y);
      y += size + 8;

      if (withDivider) {
        // Divider sits visually in the middle between heading and next block.
        y += 4;
        ensureSpace(12);
        doc.setDrawColor(225, 225, 225);
        doc.line(margin, y, pageWidth - margin, y);
        y += 8;
      }
    };

    const addSectionGap = (size = 16) => {
      y += size;
    };

    const valueColor = [98, 98, 98];
    const drawSummaryGrid = (cards) => {
      const gap = 12;
      const cardWidth = (contentWidth - gap) / 2;

      for (let i = 0; i < cards.length; i += 2) {
        const pair = cards.slice(i, i + 2);
        const cardHeights = pair.map((card) => {
          const titleLines = doc.splitTextToSize(
            toPlainText(card.title),
            cardWidth - 16,
          );
          const valueLines = doc.splitTextToSize(
            String(card.value),
            cardWidth - 16,
          );
          const descLines = doc.splitTextToSize(
            toPlainText(card.description || ""),
            cardWidth - 16,
          );

          return (
            14 +
            titleLines.length * 12 +
            8 +
            valueLines.length * 15 +
            6 +
            descLines.length * 11 +
            10
          );
        });
        const rowHeight = Math.max(...cardHeights);

        ensureSpace(rowHeight + 10);

        pair.forEach((card, idx) => {
          const x = margin + idx * (cardWidth + gap);
          const yStart = y;

          doc.setDrawColor(228, 220, 236);
          doc.roundedRect(x, yStart, cardWidth, rowHeight, 6, 6);

          const titleLines = doc.splitTextToSize(
            toPlainText(card.title),
            cardWidth - 16,
          );
          const valueLines = doc.splitTextToSize(
            String(card.value),
            cardWidth - 16,
          );
          const desc = toPlainText(card.description || "");
          const descLines = desc
            ? doc.splitTextToSize(desc, cardWidth - 16)
            : [];
          const contentHeight =
            titleLines.length * 12 +
            8 +
            valueLines.length * 15 +
            (desc ? 6 + descLines.length * 11 : 0);
          let yCard =
            yStart + Math.max(14, (rowHeight - contentHeight) / 2 + 8);

          doc.setFont(PDF_FONT_FAMILY, "bold");
          doc.setFontSize(11);
          doc.text(titleLines, x + 8, yCard);
          yCard += titleLines.length * 12 + 8;

          doc.setFont(PDF_FONT_FAMILY, "bold");
          doc.setTextColor(valueColor[0], valueColor[1], valueColor[2]);
          doc.setFontSize(15);
          doc.text(valueLines, x + 8, yCard);
          yCard += valueLines.length * 15 + 6;

          doc.setFont(PDF_FONT_FAMILY, "normal");
          doc.setTextColor(90, 90, 90);
          doc.setFontSize(10);
          if (desc) {
            doc.text(descLines, x + 8, yCard);
          }

          doc.setTextColor(32, 32, 32);
        });

        y += rowHeight + 10;
      }
    };

    const drawScenarioTable = (rows) => {
      const headers = [
        t("table.metric"),
        t("table.conservative"),
        t("table.revised"),
        t("table.optimistic"),
      ];
      const colWidths = [
        contentWidth * 0.28,
        contentWidth * 0.24,
        contentWidth * 0.24,
        contentWidth * 0.24,
      ];
      const rowPaddingY = 8;
      const lineHeight = 11;
      const xPositions = [margin];
      for (let i = 0; i < colWidths.length; i += 1) {
        xPositions.push(xPositions[i] + colWidths[i]);
      }

      const drawRow = (cells, isHeader = false, rowKind = "") => {
        const wrapped = cells.map((cell, idx) =>
          doc.splitTextToSize(toPlainText(String(cell)), colWidths[idx] - 10),
        );
        const rowHeight =
          Math.max(...wrapped.map((lines) => lines.length * lineHeight)) +
          rowPaddingY * 2;
        ensureSpace(rowHeight + 1);

        for (let i = 0; i < cells.length; i += 1) {
          const x = xPositions[i];
          const w = colWidths[i];
          doc.setDrawColor(220, 220, 220);
          if (isHeader) {
            doc.setFillColor(246, 246, 250);
            doc.rect(x, y, w, rowHeight, "FD");
          } else {
            doc.rect(x, y, w, rowHeight);
          }

          const isValueCell = !isHeader && i > 0;
          const isRoiRow = rowKind === "roi";
          const isNetBenefitRow = rowKind === "net_benefit";
          const valueUiColor = isRoiRow ? [5, 180, 255] : [98, 98, 98];
          doc.setFont(
            PDF_FONT_FAMILY,
            isHeader || i === 0
              ? "normal"
              : isRoiRow || isNetBenefitRow
                ? "bold"
                : "normal",
          );
          doc.setTextColor(
            isHeader
              ? 32
              : i === 0 && isRoiRow
                ? 5
                : isValueCell
                  ? valueUiColor[0]
                  : 45,
            isHeader
              ? 32
              : i === 0 && isRoiRow
                ? 180
                : isValueCell
                  ? valueUiColor[1]
                  : 45,
            isHeader
              ? 32
              : i === 0 && isRoiRow
                ? 255
                : isValueCell
                  ? valueUiColor[2]
                  : 45,
          );
          doc.setFontSize(isHeader ? 10 : 10.5);
          doc.text(wrapped[i], x + 5, y + rowPaddingY + lineHeight - 2);
        }

        y += rowHeight;
        doc.setTextColor(32, 32, 32);
      };

      drawRow(headers, true);
      rows.forEach((row) => {
        drawRow([row.metric, row.c, row.r, row.o], false, row.kind);
      });
      y += 10;
    };

    const drawColoredBlock = ({
      title,
      body,
      bgColor = [5, 180, 255],
      textColor = [255, 255, 255],
      stickToPageBottom = false,
    }) => {
      doc.setFont(PDF_FONT_FAMILY, "bold");
      doc.setFontSize(14);
      const titleLines = doc.splitTextToSize(
        toPlainText(title),
        contentWidth - 24,
      );
      doc.setFont(PDF_FONT_FAMILY, "normal");
      doc.setFontSize(11);
      const bodyLines = doc.splitTextToSize(
        toPlainText(body),
        contentWidth - 24,
      );
      const contentHeight = titleLines.length * 16 + 6 + bodyLines.length * 13;
      const blockHeight = contentHeight + 28;
      if (stickToPageBottom) {
        const bottomY = pageHeight - margin - blockHeight;
        if (y > bottomY) {
          doc.addPage();
          y = margin;
        }
        y = Math.max(y, bottomY);
      } else {
        ensureSpace(blockHeight + 6);
      }
      doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
      doc.roundedRect(margin, y, contentWidth, blockHeight, 8, 8, "F");

      let yText = y + (blockHeight - contentHeight) / 2 + 4;
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFont(PDF_FONT_FAMILY, "bold");
      doc.setFontSize(14);
      doc.text(titleLines, margin + 12, yText);
      yText += titleLines.length * 16 + 6;
      doc.setFont(PDF_FONT_FAMILY, "normal");
      doc.setFontSize(11);
      doc.text(bodyLines, margin + 12, yText);
      doc.setTextColor(32, 32, 32);
      if (stickToPageBottom) {
        // Force next content to start on a new page.
        y = pageHeight - margin + 2;
      } else {
        y += blockHeight + 8;
      }
    };

    const drawSummaryCardsSection = () => {
      const sectionTitle = toPlainText(
        t("page.effectiveness.conclusion.title"),
      );
      const sectionDesc = toPlainText(
        t("page.effectiveness.conclusion.description", {reps: repsDisplay}),
      );
      const containerPaddingX = 14;
      const containerPaddingTop = 16;
      const containerPaddingBottom = 16;
      const gap = 16;
      const innerWidth = contentWidth - containerPaddingX * 2;
      const topCardWidth = (innerWidth - gap * 2) / 3;
      const bottomCardWidth = contentWidth;
      const headerLines = doc.splitTextToSize(sectionDesc, innerWidth - 8);
      const topCards = conclusionItems.slice(0, 3);
      const [, , , bottomCard] = conclusionItems;

      const topLayouts = topCards.map((item) => {
        const titleLines = doc.splitTextToSize(
          toPlainText(item.title),
          topCardWidth - 20,
        );
        const bodyLines = doc.splitTextToSize(
          toPlainText(item.description),
          topCardWidth - 20,
        );
        const textHeight = titleLines.length * 12 + 10 + bodyLines.length * 11;
        return {titleLines, bodyLines, height: 18 + textHeight + 12};
      });
      const topRowHeight = Math.max(
        ...topLayouts.map((layout) => layout.height),
      );

      const bottomTitleLines = doc.splitTextToSize(
        toPlainText(bottomCard?.title || ""),
        bottomCardWidth - 20,
      );
      const bottomBodyLines = doc.splitTextToSize(
        toPlainText(bottomCard?.description || ""),
        bottomCardWidth - 20,
      );
      const bottomCardHeight =
        18 +
        (bottomTitleLines.length * 12 + 10 + bottomBodyLines.length * 11) +
        12;

      const cardsHeightTotal = topRowHeight + gap + bottomCardHeight;
      const totalHeight =
        containerPaddingTop +
        18 +
        16 +
        headerLines.length * 12 +
        18 +
        cardsHeightTotal +
        containerPaddingBottom;
      ensureSectionStart(totalHeight + 20);

      const startY = y;
      const radius = 16;
      const gradientCanvas = document.createElement("canvas");
      const canvasScale = 2;
      gradientCanvas.width = Math.max(
        1,
        Math.round(contentWidth * canvasScale),
      );
      gradientCanvas.height = Math.max(
        1,
        Math.round(totalHeight * canvasScale),
      );
      const ctx = gradientCanvas.getContext("2d");
      if (ctx) {
        const w = gradientCanvas.width;
        const h = gradientCanvas.height;
        const r = radius * canvasScale;

        ctx.beginPath();
        ctx.moveTo(r, 0);
        ctx.lineTo(w - r, 0);
        ctx.quadraticCurveTo(w, 0, w, r);
        ctx.lineTo(w, h - r);
        ctx.quadraticCurveTo(w, h, w - r, h);
        ctx.lineTo(r, h);
        ctx.quadraticCurveTo(0, h, 0, h - r);
        ctx.lineTo(0, r);
        ctx.quadraticCurveTo(0, 0, r, 0);
        ctx.closePath();
        ctx.clip();

        const gradient = ctx.createLinearGradient(0, 0, w, 0);
        gradient.addColorStop(0, "rgb(103,68,191)");
        gradient.addColorStop(1, "rgb(218,122,213)");
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, w, h);

        doc.addImage(
          gradientCanvas.toDataURL("image/png"),
          "PNG",
          margin,
          startY,
          contentWidth,
          totalHeight,
        );
      } else {
        doc.setFillColor(150, 95, 205);
        doc.roundedRect(
          margin,
          startY,
          contentWidth,
          totalHeight,
          radius,
          radius,
          "F",
        );
      }

      let yLocal = startY + containerPaddingTop + 4;
      doc.setTextColor(255, 255, 255);
      doc.setFont(PDF_FONT_FAMILY, "bold");
      doc.setFontSize(17);
      doc.text(sectionTitle, margin + containerPaddingX, yLocal);
      yLocal += 16;
      doc.setFont(PDF_FONT_FAMILY, "normal");
      doc.setFontSize(11);
      doc.text(headerLines, margin + containerPaddingX, yLocal);
      yLocal += headerLines.length * 12 + 18;

      topCards.forEach((_, index) => {
        const x = margin + containerPaddingX + index * (topCardWidth + gap);
        const yCard = yLocal;
        const cardHeight = topRowHeight;
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(225, 225, 225);
        doc.roundedRect(x, yCard, topCardWidth, cardHeight, 14, 14, "FD");
        doc.setTextColor(45, 45, 45);
        doc.setFont(PDF_FONT_FAMILY, "bold");
        doc.setFontSize(12);
        const {titleLines, bodyLines} = topLayouts[index];
        doc.text(titleLines, x + 10, yCard + 18);
        doc.setFont(PDF_FONT_FAMILY, "normal");
        doc.setFontSize(10.5);
        doc.text(bodyLines, x + 10, yCard + 36);
      });

      const bottomY = yLocal + topRowHeight + gap;
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(225, 225, 225);
      doc.roundedRect(
        margin + containerPaddingX,
        bottomY,
        bottomCardWidth - containerPaddingX * 2,
        bottomCardHeight,
        14,
        14,
        "FD",
      );
      doc.setTextColor(45, 45, 45);
      doc.setFont(PDF_FONT_FAMILY, "bold");
      doc.setFontSize(12);
      doc.text(bottomTitleLines, margin + containerPaddingX + 10, bottomY + 18);
      doc.setFont(PDF_FONT_FAMILY, "normal");
      doc.setFontSize(10.5);
      doc.text(bottomBodyLines, margin + containerPaddingX + 10, bottomY + 36);

      doc.setTextColor(32, 32, 32);
      y = startY + totalHeight + 10;
    };

    const repsDisplay = formatInteger(
      effectivenessConfigWithReps.workforce.number_of_medical_representatives,
    );

    writeTitle(
      `${t("page.effectiveness.intro_title")} - ${t("page.effectiveness.intro_desc")}`,
      18,
      false,
    );
    addSectionGap(8);

    ensureSectionStart(240);
    writeTitle(t("page.effectiveness.summary_title"), 14, false);
    writeText(`${t("page.effectiveness.calc.medical_reps")}: ${repsDisplay}`);
    writeText(
      `${t("page.effectiveness.calc.period")}: ${t("page.effectiveness.calc.period_value")}`,
    );
    drawSummaryGrid([
      {
        title: t("page.effectiveness.calc.cards.investment.title"),
        value: `${currencySymbol}${formatInteger(scenarioValues.revised.investment)}`,
        description: t("page.effectiveness.calc.cards.investment.description"),
      },
      {
        title: t("page.effectiveness.calc.cards.benefits.title"),
        value: `${currencySymbol}${formatInteger(scenarioValues.revised.benefits)}`,
        description: t("page.effectiveness.calc.cards.benefits.description"),
      },
      {
        title: t("page.effectiveness.calc.cards.net_roi.title"),
        value: `${currencySymbol}${formatInteger(scenarioValues.revised.benefit)}`,
        description: t("page.effectiveness.calc.cards.net_roi.description"),
      },
      {
        title: t("page.effectiveness.calc.cards.roi.title"),
        value: `${Math.round(scenarioValues.revised.roi)}%`,
        description: "",
      },
    ]);
    addSectionGap();

    ensureSectionStart(240);
    writeTitle(t("page.effectiveness.analysis_title"), 14, false);
    const tableRows = [
      {
        kind: "investment",
        metric: t("page.effectiveness.metric.investment"),
        c: `${currencySymbol}${formatInteger(scenarioValues.conservative.investment)}`,
        r: `${currencySymbol}${formatInteger(scenarioValues.revised.investment)}`,
        o: `${currencySymbol}${formatInteger(scenarioValues.optimistic.investment)}`,
      },
      {
        kind: "benefits",
        metric: t("page.effectiveness.metric.benefits"),
        c: `${currencySymbol}${formatInteger(scenarioValues.conservative.benefits)}`,
        r: `${currencySymbol}${formatInteger(scenarioValues.revised.benefits)}`,
        o: `${currencySymbol}${formatInteger(scenarioValues.optimistic.benefits)}`,
      },
      {
        kind: "net_benefit",
        metric: t("page.effectiveness.metric.benefit"),
        c: `${currencySymbol}${formatInteger(scenarioValues.conservative.benefit)}`,
        r: `${currencySymbol}${formatInteger(scenarioValues.revised.benefit)}`,
        o: `${currencySymbol}${formatInteger(scenarioValues.optimistic.benefit)}`,
      },
      {
        kind: "roi",
        metric: t("page.effectiveness.metric.roi"),
        c: `${formatDecimal(scenarioValues.conservative.roi)}%`,
        r: `${formatDecimal(scenarioValues.revised.roi)}%`,
        o: `${formatDecimal(scenarioValues.optimistic.roi)}%`,
      },
      {
        kind: "payback",
        metric: t("page.effectiveness.metric.months"),
        c: formatPaybackPeriod(scenarioValues.conservative.months),
        r: formatPaybackPeriod(scenarioValues.revised.months),
        o: formatPaybackPeriod(scenarioValues.optimistic.months),
      },
    ];
    drawScenarioTable(tableRows);
    addSectionGap();

    ensureSectionStart(140);
    drawColoredBlock({
      title: toPlainText(t("page.effectiveness.recommendations_title")),
      body: toPlainText(keyRecommendationDescriptionHtml),
      bgColor: [5, 180, 255],
      textColor: [255, 255, 255],
      stickToPageBottom: true,
    });
    addSectionGap(16);

    const renderSection = (
      section,
      recommendations,
      keepWholeOnNextPage = false,
    ) => {
      if (keepWholeOnNextPage) {
        const estimatedSectionHeight =
          120 + section.items.length * 120 + (recommendations ? 130 : 0);
        ensureSectionStart(estimatedSectionHeight);
      }
      writeTitle(toPlainText(section.title), 14, false);
      section.items.forEach((item) => {
        const headText = toPlainText(item.head).replace(/\s+/g, " ").trim();
        const valueText =
          `${toPlainText(item.valueLabel)} ${toPlainText(item.value)}`
            .replace(/\s+/g, " ")
            .trim();
        const rightWidth = doc.getTextWidth(valueText);
        const leftMaxWidth = Math.max(40, contentWidth - rightWidth - 16);
        const headLine = doc.splitTextToSize(headText, leftMaxWidth)[0] || "";
        const bodyLines = doc.splitTextToSize(
          toPlainText(item.body),
          contentWidth,
        );

        const headingRowHeight = 12 + 6; // writeSectionRowSpaceBetween
        const bodyHeight = bodyLines.length * (10 + 2) + 8; // writeText(..., 10, 8)
        const itemTotalHeight = headingRowHeight + bodyHeight + 2;

        // Keep each accordion item intact on one page:
        // if the whole item doesn't fit, move heading/value row too.
        if (y + itemTotalHeight > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }

        writeSectionRowSpaceBetween(headLine, valueText);
        writeText(toPlainText(item.body), 10, 8);
      });
      writeRightAlignedTotal(toPlainText(section.total));

      if (recommendations) {
        drawColoredBlock({
          title: toPlainText(recommendations.title),
          body: toPlainText(recommendations.description),
          bgColor: [5, 180, 255],
          textColor: [255, 255, 255],
          stickToPageBottom: true,
        });
      }
      addSectionGap(20);
    };

    renderSection(costSavingsSection, undefined, true);
    renderSection(impactSection);
    renderSection(analysisSection, pricingNoteRecommendations);

    drawSummaryCardsSection();

    const REF_PDF = "page.effectiveness.references";

    if (!langIsRu) {
      doc.addPage();
      y = margin;
      writeTitle(toPlainText(t(`${REF_PDF}.title`)), 14, false);
      writeText(toPlainText(t(`${REF_PDF}.intro`)), 10, 8);
      addSectionGap(14);

      writeTitle(toPlainText(t(`${REF_PDF}.industry_title`)), 12, false);
      const pdfIndustry = t(`${REF_PDF}.industry_items`, {returnObjects: true});
      if (Array.isArray(pdfIndustry)) {
        pdfIndustry.forEach((row) => {
          writeText(
            toPlainText(`${row.lead} ${row.url}`),
            10,
            6,
          );
        });
      }
      addSectionGap(12);

      writeTitle(toPlainText(t(`${REF_PDF}.training_title`)), 12, false);
      const pdfTraining = t(`${REF_PDF}.training_items`, {returnObjects: true});
      if (Array.isArray(pdfTraining)) {
        pdfTraining.forEach((row) => {
          writeText(
            toPlainText(`${row.lead} ${row.url}`),
            10,
            6,
          );
        });
      }
      addSectionGap(12);

      writeTitle(toPlainText(t(`${REF_PDF}.compensation_title`)), 12, false);
      writeText(toPlainText(t(`${REF_PDF}.compensation_intro`)), 10, 6);
      const pdfComp = t(`${REF_PDF}.compensation_sources`, {
        returnObjects: true,
      });
      if (Array.isArray(pdfComp)) {
        pdfComp.forEach((line) => {
          writeText(`• ${toPlainText(line)}`, 10, 6);
        });
      }
    }

    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  }, [
    analysisSection,
    conclusionItems,
    costSavingsSection,
    currencySymbol,
    effectivenessConfigWithReps.workforce.number_of_medical_representatives,
    formatDecimal,
    formatInteger,
    formatPaybackPeriod,
    impactSection,
    keyRecommendationDescriptionHtml,
    pricingNoteRecommendations,
    scenarioValues,
    t,
    langIsRu,
  ]);

  // Парольный попап при входе на страницу отключён — страница сразу видна.
  // useEffect(() => {
  //   if (!isUnlocked) {
  //     openPopup(<EffectivenessPopup onSuccess={() => setIsUnlocked(true)} />, {
  //       closeButton: true,
  //       onClose: () => navigate(-1),
  //     });
  //   } else {
  //     closePopup();
  //   }
  //   return () => {
  //     closePopup();
  //   };
  // }, [isUnlocked, openPopup, closePopup, navigate]);

  return (
    <>
      <Helmet title={t("head_title.effectiveness")} />

      <EffectivenessContext.Provider value={model}>
        <div className={"container-big"}>
          <div className={styles.effectiveness}>
            <div
              id={"effectiveness-pdf-content"}
              className={"flex flex-col gap-[120rem]"}
            >
              <Intro />

              <div className={"container-home"}>
                <div className={styles.effectiveness_calc_wrapper}>
                  <Calc onPrint={handlePrint} />

                  <Analysis />

                  <article
                    style={{
                      backgroundColor: "#05B4FF",
                      borderRadius: "40rem",
                    }}
                  >
                    <H3>
                      <span
                        style={{color: "#fff"}}
                        dangerouslySetInnerHTML={{
                          __html: t("page.effectiveness.recommendations_title"),
                        }}
                      />
                    </H3>

                    <RootDesc>
                      <span
                        style={{
                          color: "#fff",
                          fontSize: "23rem",
                          fontWeight: "400",
                          lineHeight: "120%",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: keyRecommendationDescriptionHtml,
                        }}
                      />
                    </RootDesc>
                  </article>
                </div>
              </div>

              <div className={"container-home"}>
                <AccordeonSection
                  title={costSavingsSection.title}
                  items={costSavingsSection.items}
                  total={costSavingsSection.total}
                />
              </div>

              <div className={"container-home"}>
                <AccordeonSection
                  title={impactSection.title}
                  items={impactSection.items}
                  total={impactSection.total}
                />
              </div>

              <div className={"container-home"}>
                <AccordeonSection
                  title={analysisSection.title}
                  items={analysisSection.items}
                  total={analysisSection.total}
                  recommendations={pricingNoteRecommendations}
                />
              </div>

              <div className={"container-home"}>
                <Conclusion />
              </div>
            </div>

            <div
              className={"container-home"}
              style={{maxWidth: "1360rem"}}
            >
              <Try
                onClickBtn={handleOpenPopup}
                button={t("button.effectiveness_try")}
                size={35}
                title={
                  <b
                    dangerouslySetInnerHTML={{
                      __html: t("page.effectiveness.try"),
                    }}
                  />
                }
              />
            </div>

            {!langIsRu && (
              <div
                className={"container-home"}
                style={{maxWidth: "1360rem"}}
              >
                <Libs />
              </div>
            )}
          </div>
        </div>
      </EffectivenessContext.Provider>
    </>
  );
};
